<template>
    <div>
        <div class="mb-4 flex items-center justify-between space-x-2">
            <h1 class="text-lg font-semibold text-gray-700">Providers</h1>
            <div class="flex items-center space-x-2">
                <UInput
                    v-model="search"
                    type="text"
                    placeholder="Search..."
                    icon="i-ph-magnifying-glass-duotone"
                />
                <UButton @click="overlays.create = true" color="black" icon="i-ph-plus-bold">
                    Add Provider
                </UButton>
            </div>
        </div>

        <div class="bg-white ring-1 ring-black/5 rounded-lg shadow-sm">
            <UTable
                :rows="providers"
                :columns="columns"
                :loading="loading"
                :loading-state="{ icon: 'i-ph-circle-notch-bold', label: '' }"
                :empty-state="{ icon: 'i-ph-ambulance-duotone', label: 'No providers.' }"
                :ui="{ td: { padding: 'py-3' } }"
            >
                <template #avatar-data="{ row }">
                    <div class="flex flex-col items-center">
                        <Avatar bucket="media" folder="providers" size="md" :placeholder="row.name" :path="row.avatar" />
                    </div>
                </template>
                <template #emails-data="{ row }">
                    <div class="space-y-0.5">
                        <p v-for="(email, i) in (row.emails || [])" :key="i" class="text-sm text-gray-600">{{ email }}</p>
                    </div>
                </template>
                <template #commission_percentage-data="{ row }">
                    <p class="font-mono text-right">
                        {{ row.commission_percentage }}%
                    </p>
                </template>
                <template #transports-data="{ row }">
                    <p class="font-mono text-right">
                        {{ row.transports?.length ? row.transports[0].count : 0 }}
                    </p>
                </template>
                <template #actions-data="{ row }">
                    <UDropdown :items="actions(row)">
                        <UButton color="gray" variant="ghost" icon="i-heroicons-ellipsis-horizontal-20-solid" />
                    </UDropdown>
                </template>
            </UTable>
        </div>

        <OverlayProviderCreateProvider v-model:open="overlays.create" @reload="fetchData" />
        <OverlayProviderEditProvider v-model:open="overlays.edit" @reload="fetchData" :provider="selectedProvider" />
        <OverlayProviderRemoveProvider v-model:open="overlays.remove" @reload="fetchData" :provider="selectedProvider" />
        <OverlayProviderManagePricing v-model:open="overlays.pricing" :provider="selectedProvider" />
    </div>
</template>

<script setup lang="ts">
import { watchDebounced } from '@vueuse/core'
import { UserRole } from '~/types/user'
import type { Provider } from '~/types/provider'

definePageMeta({ layout: 'default', middleware: 'auth', roles: [UserRole.admin] })
useHead({ title: 'Settings – Providers' })

const client = useSupabaseClient()

const loading = ref(false)
const search = ref('')
const providers = ref<Provider[]>([])
const selectedProvider = ref<Provider>()

const overlays = reactive({
    create: false,
    edit: false,
    remove: false,
    pricing: false,
})

const columns = [
    { key: 'avatar', class: 'w-px' },
    { key: 'name', label: 'Name', class: 'w-full' },
    { key: 'emails', label: 'Emails' },
    { key: 'commission_percentage', label: 'Commission' },
    { key: 'transports', label: 'Transports' },
    { key: 'actions', class: 'w-px' },
]

const actions = (provider: Provider) => [
    [
        {
            label: 'Manage Pricing',
            icon: 'i-ph-currency-dollar-fill',
            click: () => {
                selectedProvider.value = provider
                overlays.pricing = true
            },
        },
        {
            label: 'Edit',
            icon: 'i-ph-pencil-fill',
            click: () => {
                selectedProvider.value = provider
                overlays.edit = true
            },
        },
        {
            label: 'Remove',
            icon: 'i-ph-trash-fill',
            iconClass: 'text-red-400',
            click: () => {
                selectedProvider.value = provider
                overlays.remove = true
            },
        },
    ],
]

onMounted(() => fetchData())
watchDebounced(search, () => fetchData(), { debounce: 600 })

async function fetchData() {
    loading.value = true

    let query = client.from('providers')
        .select(`
            id, number, name, emails, avatar, commission_percentage, metadata, updated_at,
            transports (count)
        `, { count: 'exact' })
        .is('deleted_at', null)
        .order('name')

    if (search.value) {
        query = query.ilike('name', `%${search.value}%`)
    }

    const { data } = await query

    if (data) {
        providers.value = data as any
    }

    loading.value = false
}
</script>
